import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { withSnackbar } from 'notistack';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import AuthService from 'services/auth.service';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';

import { verifyPassword } from 'plugins/validation/validation-rules';
import layoutNames from 'utils/enums/layout-names.enum';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Button from 'components/CustomButtons/Button';
import CustomInput from 'components/CustomInput/CustomInput';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardFooter from 'components/Card/CardFooter';
import SnackMessage from 'components/Snackbar/SnackMessage';

import styles from 'assets/jss/material-dashboard-pro-react/views/registerPageStyle';

const localStyles = {
	inputWrapper: {
		width: '100%',
		display: 'inline-flex',
		transition: 'margin-bottom 0.3s ease',
	},
	inputWrapperBig: {
		marginBottom: '20px',
		"@media (max-width: 768px)": {
			marginBottom: '35px',
		},
		"@media (max-width: 450px)": {
			marginBottom: '60px',
		},
	},
	container: {
		position: 'relative',
		zIndex: '3',
		paddingRight: "15px",
		paddingLeft: "15px",
		marginRight: "auto",
		marginLeft: "auto",
		width: '1200px',
		"@media (max-width: 450px)": {
			paddingRight: "5px",
			paddingLeft: "5px",
		},
	},
};

const useStyles = makeStyles(styles);
const useLocalStyles = makeStyles(localStyles);

const passwordErrorMessage = 'Password should be at least 8 characters long and contain lower and upper case letters, at least one number and one character ( ! " # $ % & \' ( ) * +)';

const ChangePasswordPage = ({ enqueueSnackbar, username }) => {
	const classes = { ...useStyles(), ...useLocalStyles() };
	const { control, watch, trigger, handleSubmit, formState } = useForm();
	const { errors } = formState;
	const history = useHistory();
	const location = useLocation();
	const [loading, setLoading] = React.useState(false);

	const watchPassword = watch('password');
	const currentUsername = username || location.state?.username;

	const afterSubmit = () => {
		setLoading(false);
		AuthService.logout();
		history.push(`/${layoutNames.auth}`);
		const successMessages = ['Your password has been updated successfully!'];
		enqueueSnackbar(successMessages, {
			content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
		});
	};

	const showErrorMessages = (errorMessages) => {
		enqueueSnackbar(errorMessages, {
			content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
		});
	};

	const setWrapperStyle = (fieldName) => {
		const MAX_ERROR_LENGTH = 40;
		if (errors && errors[fieldName] && errors[fieldName].message.length > MAX_ERROR_LENGTH) {
			return `${classes.inputWrapper} ${classes.inputWrapperBig}`;
		}
		return classes.inputWrapper;
	};

	const onSubmit = async ({
		oldPassword,
		password,
		repeatPassword,
	}) => {
		if (loading) return;
		if (currentUsername) {
			const data = {
				newPassword: password,
				username: currentUsername,
				oldPassword,
				repeatPassword,
			};
			setLoading(true);
			try {
				await AuthService.changePassword(data);
				afterSubmit();
			} catch (e) {
				showErrorMessages(e.resolvedErrorMessage);
			}
			setLoading(false);
		} else {
			const errorMessages = ['You must be logged in.'];
			showErrorMessages(errorMessages);
		}
	};

	return (
		<div className={classes.container}>
			<GridContainer justify='center'>
				<GridItem xs={10} sm={8} md={6}>
					<Card>
						<CardHeader
							color='rose'
							className={`${classes.inlineBlock} ${classes.center}`}
						>
							<h4 className={classes.inlineBlock}>Change Password</h4>
						</CardHeader>
						<form onSubmit={handleSubmit(onSubmit)} noValidate>
							<input type='hidden' value='prayer' />
							<CardBody>
								<GridContainer justify='center'>
									<GridItem>
										<div className={setWrapperStyle('oldPassword')}>
											<Controller
												name="oldPassword"
												control={control}
												defaultValue={''}
												rules={{
													required: 'Field should not be empty',
												}}
												render={({ onChange, value }) => (
													<CustomInput
														labelText={'Old Password *'}
														error={!!errors.oldPassword}
														helperText={errors.oldPassword ? errors.oldPassword.message : ""}
														formControlProps={{
															fullWidth: true,
															className: classes.customFormControlClasses
														}}
														inputProps={{
															type: 'password',
															autoComplete: 'new-oldPassword',
															value: value,
														}}
														onChange={(event) => {
															const newValue = event.target.value?.trimStart();
															onChange(newValue);
															trigger('oldPassword');
														}}
														onBlur={() => {
															trigger('oldPassword');
														}}
													/>
												)}
											/>
										</div>
										<div className={setWrapperStyle('password')}>
											<Controller
												name="password"
												control={control}
												defaultValue={''}
												rules={{
													required: 'Field should not be empty',
													maxLength: {
														value: 255,
														message: 'Password max length is 255 characters',
													},
													validate: (value) => {
														return verifyPassword(value) || passwordErrorMessage;
													},
												}}
												render={({ onChange, value }) => (
													<CustomInput
														labelText={'Password *'}
														error={!!errors.password}
														helperText={errors.password ? errors.password.message : ""}
														formControlProps={{
															fullWidth: true,
															className: classes.customFormControlClasses
														}}
														inputProps={{
															type: 'password',
															autoComplete: 'new-password',
															value: value,
														}}
														onChange={(event) => {
															const newValue = event.target.value?.trimStart();
															onChange(newValue);
															trigger('password');
															trigger('repeatPassword');
														}}
														onBlur={() => {
															trigger('password');
															trigger('repeatPassword');
														}}
													/>
												)}
											/>
										</div>
										<div className={setWrapperStyle('repeatPassword')}>
											<Controller
												name="repeatPassword"
												control={control}
												defaultValue={''}
												rules={{
													required: 'Field should not be empty',
													validate: (value) => {
														return value === watchPassword || 'Please, make sure your password match';
													},
												}}
												render={({ onChange, value }) => (
													<CustomInput
														labelText={'Repeat Password *'}
														error={!!errors.repeatPassword}
														helperText={errors.repeatPassword ? errors.repeatPassword.message : ""}
														formControlProps={{
															fullWidth: true,
															className: classes.customFormControlClasses
														}}
														inputProps={{
															type: 'password',
															autoComplete: 'new-repeatPassword',
															value: value,
														}}
														onChange={(event) => {
															const newValue = event.target.value?.trimStart();
															onChange(newValue);
															trigger('repeatPassword');
														}}
														onBlur={() => {
															trigger('repeatPassword');
														}}
													/>
												)}
											/>
										</div>
									</GridItem>
								</GridContainer>
							</CardBody>
							<CardFooter className={classes.justifyContentCenter}>
								<Button disabled={loading} color='rose' simple size='lg' block type='submit'>
									Continue
                </Button>
							</CardFooter>
						</form>
					</Card>
				</GridItem>
			</GridContainer>
		</div>
	);
}

const mapStateToProps = (state) => ({
	username: state.auth.auth?.username,
});

export default connect(mapStateToProps)(withSnackbar(ChangePasswordPage));
